import { gql } from '@apollo/client';

export const CREATE_PHARMACY = gql`
  mutation CreatePharmacy($data: PharmacyCreateInput!, $accountNumber: String) {
    createPharmacy(data: $data, accountNumber: $accountNumber) {
      id
      deaNumber
      email
      fax
      federalTaxId
      legalName
      mobile
      ncpdp
      npi
      phone
      site
      softwareName
      store
    }
  }
`;

export const PHARMACIES_LIST_ORDER_BY = [{ createdAt: 'asc' as const }];

export const PHARMACIES = gql`
  query FindManyPharmacy(
    $where: PharmacyWhereInput
    $orderBy: [PharmacyOrderByWithRelationInput!]
  ) {
    findManyPharmacy(where: $where, orderBy: $orderBy) {
      id
      deaNumber
      email
      legalName
      phone
      createdAt
      PharmacyStatus {
        id
        Status {
          key
          id
          name
        }
      }
      PharmacyUsers {
        User {
          firstName
          lastName
        }
      }
    }
  }
`;

export const UPDATE_PHARMACY = gql`
  mutation UpdatePharmacy($data: PharmacyUpdateInput!, $where: PharmacyWhereUniqueInput!, $accountNumber: String) {
    updatePharmacy(data: $data, where: $where, accountNumber: $accountNumber) {
      id
      deaNumber
      email
      fax
      federalTaxId
      legalName
      mobile
      ncpdp
      npi
      phone
      site
      softwareName
      store
    }
  }
`;

export const PHARMACY_OPTIONS = gql`
  query pharmacyOptions {
    findManyState {
      id
      name
    }
    findManyPharmacyType {
      id
      name
    }
    findManyStatement {
      id
      question
      defaultAnswer
    }
    findFirstAddressType(where: { key: {equals: "physical"} }) {
      id
    }
    findManyStatus {
      key
      id
      name
    }
  }
`;

export const BANK_ACCOUNT = gql`
  query FindFirstBankAccount($where: BankAccountWhereInput) {
    findFirstBankAccount(where: $where) {
      id
      accountId
      accountHolderName
      bankName
      last4
      status
      object
    }
  }
`;

export const SELLING_LIMITATIONS = gql`
  query SellingLimitations($pharmacyId: Int!) {
    sellingLimitations(pharmacyId: $pharmacyId) {
      pharmacyId
      blockAllPostings
      ndcs
      restrictedManufacturers
      deactivatedCount
    }
  }
`;

export const UPDATE_SELLING_LIMITATIONS = gql`
  mutation UpdateSellingLimitations(
    $pharmacyId: Int!
    $blockAllPostings: Boolean!
    $ndcs: [String!]
    $restrictedManufacturers: [String!]
  ) {
    updateSellingLimitations(
      pharmacyId: $pharmacyId
      blockAllPostings: $blockAllPostings
      ndcs: $ndcs
      restrictedManufacturers: $restrictedManufacturers
    ) {
      pharmacyId
      blockAllPostings
      ndcs
      restrictedManufacturers
      deactivatedCount
    }
  }
`;

export const UNIQUE_USER = gql`
  query FindUniqueUser($where: UserWhereUniqueInput!) {
    findUniqueUser(where: $where) {
      id
      firstName
      lastName
      PharmacyUsers {
        pharmacyId
        Pharmacy {
          id
          legalName
          email
          PharmacyStatus {
            id
            Status {
              key
              name
            }
          }
          PharmacyFiles {
            id
            File {
              id
              key
              name
              type
            }
          }
          cardPaymentEnabled
          BankAccount {
            id
            accountHolderName
            accountId
            bankName
            country
            currency
            customer
            hostedVerificationUrl
            last4
            object
            routingNumber
            status
            token
          }
        }
      }
      UserRoles {
        Role {
          key
          name
        }
      }
    }
  }
`;

export const PHARMACY_USERS = gql`
  query FindManyPharmacyUsers($where: PharmacyUsersWhereInput, $orderBy: [PharmacyUsersOrderByWithRelationInput!]) {
     findManyPharmacyUsers(where: $where, orderBy: $orderBy) {
        User {
          id
          email
          firstName
          lastName
          phone
          UserRoles {
            Role {
              key
              name
            }
          }
        }
     }
  }
`

export const UNIQUE_PHARMACY = gql`
  query FindUniquePharmacy($where: PharmacyWhereUniqueInput!) {
    findUniquePharmacy(where: $where) {
      id
      deaNumber
      email
      fax
      federalTaxId
      legalName
      mobile
      ncpdp
      npi
      phone
      site
      softwareName
      store
      PharmacyStatus {
        Status {
          id
          key
          name
        }
      }
      PharmacyAddress {
        State {
          id
          name
        }
        id
        address
        city
        zipCode
      }
      PharmacyDetail {
        id
        contractManager
        managerContactName
        is340B
        PharmacyDetailTypes {
          PharmacyType {
            id
          }
        }
      }
      PharmacyLicensedStates {
        id
        State {
          id
          name
        }
      }
      PharmacyStatement {
        id
        answer
        statementId
        Statement {
          question
        } 
      }
      PharmacyLicenses {
        deaLicenseDate
        ncpdpLicenseDate
      }
      PharmacyFiles {
        id
        File {
          id
          key
          name
          type
        }
      }
      cardPaymentEnabled
      BankAccount {
        id
        accountHolderName
        bankName
        routingNumber
        last4
        object
        status
      }
      AdditionalQuestions {
        id
        question
        answer
        additionalAnswer
      }
    }
  }
`

export const FIND_PHARMACY = gql`
  query FindFirstPharmacy($where: PharmacyWhereInput!) {
    findFirstPharmacy(where: $where) {
      id
      deaNumber
      ncpdp
    }
  }
`

export const CHECKOUT_PHARMACY = gql`
  query CheckoutPharmacy($where: PharmacyWhereUniqueInput!) {
    findUniquePharmacy(where: $where) {
      id
      cardPaymentEnabled
      email
    }
  }
`

export const STATUSES = gql`
   query Statuses {
    findManyStatus {
      key
      id
      name
    }
  }
`;

export const TOGGLE_CARD_PAYMENT = gql`
  mutation ToggleCardPayment($pharmacyId: Int!, $enabled: Boolean!) {
    toggleCardPayment(pharmacyId: $pharmacyId, enabled: $enabled) {
      id
      cardPaymentEnabled
    }
  }
`;

export const ACTIVE_PHARMACY_VACATIONS = gql`
  query ActivePharmacyVacations {
    activePharmacyVacations {
      pharmacyId
      vacationStartAt
      vacationEndAt
    }
  }
`;